import React from 'react';
import { Page, View, Text, StyleSheet, Svg, Path } from '@react-pdf/renderer';
import { ReportData } from '../../../types/report.types';

interface CertificatePageProps {
  reportData: ReportData;
  sectionId?: string;
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: '16mm 14mm',
    paddingBottom: '22mm',
    fontFamily: 'Roboto',
    fontSize: 9.5,
    color: '#4B5563',
    position: 'relative',
  },
  pageNumFooter: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    fontSize: 8.5,
    color: '#9CA3AF',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  footerLeft: {
    textAlign: 'left',
  },
  footerRight: {
    textAlign: 'right',
  },
  certBox: {
    borderWidth: 1.5,
    borderColor: '#3B82F6',
    borderRadius: 12,
    padding: 24,
    backgroundColor: '#F8FAFC',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    marginBottom: 16,
  },
  checkMarkCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#3B82F6',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    marginBottom: 8,
  },
  certHeadingLabel: {
    fontSize: 8,
    color: '#3B82F6',
    fontWeight: 'bold',
    letterSpacing: 1.5,
    textAlign: 'center',
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  certSubHeadingLabel: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0F172A',
    textAlign: 'center',
    marginBottom: 12,
  },
  certIntro: {
    fontSize: 10.5,
    color: '#475569',
    lineHeight: 1.50,
    textAlign: 'center',
    marginBottom: 16,
    width: '95%',
    alignSelf: 'center',
  },
  detailsTable: {
    width: '100%',
    borderWidth: 1.2,
    borderColor: '#CBD5E1',
    backgroundColor: '#FFFFFF',
    marginBottom: 16,
  },
  detailRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    minHeight: 32,
    alignItems: 'center',
  },
  detailLabel: {
    width: '30%',
    fontSize: 9.5,
    fontWeight: 'bold',
    color: '#3B82F6',
    paddingHorizontal: 12,
    paddingVertical: 8,
    textTransform: 'capitalize',
  },
  detailValue: {
    width: '70%',
    fontSize: 9.5,
    color: '#1F2937',
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontWeight: 'bold',
    lineHeight: 1.4,
  },
  certDisclaimer: {
    fontSize: 9.0,
    color: '#64748B',
    lineHeight: 1.45,
    textAlign: 'center',
    width: '95%',
    alignSelf: 'center',
    marginBottom: 24,
  },
  signatureRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    gap: 40,
    marginTop: 'auto', // Pushes signature row to bottom of container
    paddingHorizontal: 20,
    marginBottom: 10,
  },
  signatureBlock: {
    width: '45%',
    alignItems: 'center',
  },
  sigLine: {
    borderBottomWidth: 1.2,
    borderBottomColor: '#9CA3AF',
    width: '100%',
    marginTop: 6,
    marginBottom: 6,
  },
  sigName: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#0F172A',
    textAlign: 'center',
  },
  sigRole: {
    fontSize: 7.5,
    color: '#9CA3AF',
    textAlign: 'center',
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 14,
    right: 14,
  },
  footerLine: {
    borderBottomWidth: 1.2,
    borderBottomColor: '#1E293B',
    width: '100%',
    marginBottom: 6,
  },
  footerText: {
    fontSize: 7.5,
    color: '#9CA3AF',
    textAlign: 'left',
  },
});

const CertificatePageV2: React.FC<CertificatePageProps> = ({ reportData, sectionId }) => {
  const certConfig: any = reportData.finding_config?.certificate || {};

  const targets = reportData.target_details || [];
  const isNetworkTarget = (t: any) => {
    const type = (t.target_type || t.type || '').toLowerCase();
    const value = (t.target_value || t.url || t.target_name || '').toLowerCase();
    if (type.includes('web') || type.includes('api') || type.includes('app') || type.includes('mobile') || type.includes('ios') || type.includes('android')) {
      return false;
    }
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return false;
    }
    return type.includes('network') || type.includes('infra') || type.includes('subnet') || type.includes('range') || type.includes('ip') ||
           !!value.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/) ||
           /\/\s*\d{1,2}\s*$/.test(value);
  };

  const networkTargets = targets.filter(isNetworkTarget);
  const webTargets = targets.filter(t => !isNetworkTarget(t));
  const isNetwork = reportData.testing_type?.toLowerCase().includes('network') ||
    reportData.program_details?.[0]?.testing_type?.toLowerCase().includes('network') || (networkTargets.length > 0 && webTargets.length === 0);

  const testingOrg = reportData.branding_company || 'Capture The Bug Ltd.';
  const client = reportData.company_name || 'Client Organisation';
  const targetName = reportData.program_name || reportData.report_title || 'Target Application';
  const pentestType = reportData.testing_type || reportData.program_details?.[0]?.testing_type || 'Web Application Penetration Test';
  
  const formatCertDate = (d: any) => {
    if (!d) return '25 Jun 2026';
    const date = new Date(d);
    if (isNaN(date.getTime())) return String(d);
    const day = String(date.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const formattedStart = formatCertDate(reportData.start_date);
  const formattedEnd = formatCertDate(reportData.end_date);
  const testingPeriod = reportData.start_date && reportData.end_date
    ? `${formattedStart} - ${formattedEnd}`
    : formatCertDate(reportData.current_date);

  const docNumber = reportData.document_number || 'WAPT-2026-014';
  const version = reportData.version_number || '1.0';
  const defaultReportRef = `${docNumber} / v${version}`;

  // Build finding summary string
  const counts = {
    Critical: (reportData.severity_counts as any)?.Critical || reportData.open_close_counts_by_severity?.Critical?.Total || 0,
    High: (reportData.severity_counts as any)?.High || reportData.open_close_counts_by_severity?.High?.Total || 0,
    Medium: (reportData.severity_counts as any)?.Medium || reportData.open_close_counts_by_severity?.Medium?.Total || 0,
    Low: (reportData.severity_counts as any)?.Low || reportData.open_close_counts_by_severity?.Low?.Total || 0,
    Info: (reportData.severity_counts as any)?.Info || reportData.open_close_counts_by_severity?.Info?.Total || 0,
  };

  const findingSummary = [
    counts.Critical > 0 ? `${counts.Critical} Critical` : null,
    counts.High > 0 ? `${counts.High} High` : null,
    counts.Medium > 0 ? `${counts.Medium} Medium` : null,
    counts.Low > 0 ? `${counts.Low} Low` : null,
    counts.Info > 0 ? `${counts.Info} Info` : null,
  ].filter(Boolean).join(', ') || 'No findings';

  const defaultAssessment = isNetwork
    ? `Internal Network Penetration Test (Assumed-breach)`
    : `${pentestType} (Grey-box Web Application)`;

  // Dynamic Scope for network targets
  const subnetTargetsCount = networkTargets.filter((t: any) => {
    const val = (t.target_value || t.url || '').toLowerCase();
    return val.includes('/') || val.includes('-');
  }).length;
  const subnetsText = subnetTargetsCount > 0 ? `${subnetTargetsCount} subnet${subnetTargetsCount > 1 ? 's' : ''}` : `${targets.length} target${targets.length > 1 ? 's' : ''}`;
  const totalAddresses = reportData.finding_config?.networkScopeSummary?.totalAddresses || String(targets.length || 0);
  const liveHosts = reportData.finding_config?.networkScopeSummary?.liveHosts || String(targets.length || 0);
  const defaultNetworkScope = `${subnetsText} (~${totalAddresses} addresses); ${liveHosts} live hosts \u2014 see Appendix A`;

  const defaultApplication = `${targetName} - https://app.contososhop.com`;
  const defaultResultSummary = `${findingSummary} findings - detailed in the accompanying report`;

  // Apply certConfig overrides or default values
  const assessmentVal = certConfig.assessment || defaultAssessment;
  const applicationVal = isNetwork ? (certConfig.scope || defaultNetworkScope) : (certConfig.application || defaultApplication);
  const testingPeriodVal = certConfig.testingPeriod || testingPeriod;
  const methodologyVal = certConfig.methodology || (isNetwork ? 'PTES · NIST SP 800-115 · MITRE ATT&CK · CVSS v3.1' : 'OWASP WSTG · OWASP Top 10 (2021) · CVSS v3.1');
  const resultSummaryVal = certConfig.resultSummary || defaultResultSummary;
  const reportRefVal = certConfig.reportRef || defaultReportRef;

  const preparedBy = certConfig.signer1Name || reportData.prepared_by || reportData.test_lead || 'J. Singh';
  const reviewedBy = certConfig.signer2Name || reportData.reviewed_by || 'R. Mehta';
  const currentDate = formatCertDate(reportData.current_date);

  const defaultNetworkIntroText = `This is to certify that ${testingOrg} has completed an internal network penetration test on behalf of ${client}. The assessment was conducted in accordance with PTES and NIST SP 800-115 and industry best practices.`;
  const defaultWebIntroText = `This is to certify that ${testingOrg} has completed a web application penetration test of ${targetName} on behalf of ${client}. The assessment was conducted in accordance with the OWASP Web Security Testing Guide (WSTG) and industry best practices.`;
  const introText = certConfig.introText || (isNetwork ? defaultNetworkIntroText : defaultWebIntroText);

  const detailRows = [
    { label: 'Assessment', value: assessmentVal },
    { label: isNetwork ? 'Scope' : 'Application', value: applicationVal },
    { label: 'Testing period', value: testingPeriodVal },
    { label: 'Methodology', value: methodologyVal },
    { label: 'Result summary', value: resultSummaryVal },
    { label: 'Report reference', value: reportRefVal },
  ];

  return (
    <Page size="A4" id={sectionId} style={styles.page}>
      {/* Blue Certificate Border Box */}
      <View style={styles.certBox}>
        {/* Checkmark icon as SVG shape */}
        <View style={styles.checkMarkCircle}>
          <Svg viewBox="0 0 24 24" style={{ width: 18, height: 18 }}>
            <Path
              d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"
              fill="#FFFFFF"
            />
          </Svg>
        </View>

        {/* Header titles */}
        <Text style={styles.certHeadingLabel}>Certificate of Attestation</Text>
        <Text style={styles.certSubHeadingLabel}>Penetration Test Completion</Text>

        {/* Narrative Intro */}
        <Text style={styles.certIntro}>
          {introText}
        </Text>

        {/* Details List/Table */}
        <View style={styles.detailsTable}>
          {detailRows.map((row, idx) => (
            <View key={idx} style={[styles.detailRow, { borderBottomWidth: idx === detailRows.length - 1 ? 0 : 1 }]}>
              <Text style={styles.detailLabel}>{row.label}</Text>
              <Text style={styles.detailValue}>{row.value}</Text>
            </View>
          ))}
        </View>

        {/* Attestation Disclaimer */}
        <Text style={styles.certDisclaimer}>
          {certConfig.middleDisclaimer || 'Identified vulnerabilities and remediation guidance are detailed in the accompanying report. This certificate attests that testing was performed for the stated scope and period; it does not guarantee the absence of all vulnerabilities. A retest of remediated items is recommended to validate fixes.'}
        </Text>

        {/* Signatures */}
        <View style={styles.signatureRow}>
          <View style={styles.signatureBlock}>
            <Text style={styles.sigName}>{preparedBy}</Text>
            <View style={styles.sigLine} />
            <Text style={styles.sigRole}>{certConfig.signer1Title || 'Lead Penetration Tester'} · {currentDate}</Text>
          </View>
          <View style={styles.signatureBlock}>
            <Text style={styles.sigName}>{reviewedBy}</Text>
            <View style={styles.sigLine} />
            <Text style={styles.sigRole}>{certConfig.signer2Title || 'Reviewed & Approved'} · {currentDate}</Text>
          </View>
        </View>
      </View>

      {/* Footer at bottom of page */}
      <View style={styles.footer}>
        <View style={styles.footerLine} />
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Text style={[styles.footerText, { flex: 1 }]}>
            {certConfig.disclaimer || `${testingOrg} - Confidential. Prepared for ${client}. Replace all placeholders before delivery.`}
          </Text>
          <Text style={{ fontSize: 8, color: '#9CA3AF', marginLeft: 15 }} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
        </View>
      </View>
    </Page>
  );
};

export default CertificatePageV2;
